import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Eye, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApi, errMsg } from "@/lib/api";

const ClauseRow = ({ c, onUpload, onView, onDelete, busy }) => {
  const ref = useRef(null);
  const has = !!c.filename;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`clause-row-${c.category}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{c.category}</div>
        <div className="text-xs text-slate-400 truncate" data-testid={`clause-file-${c.category}`}>
          {has ? `${c.filename}${c.pages ? ` · ${c.pages} pagine` : ""}` : "Nessun file: il PDF non avrà pagine di condizioni"}
        </div>
      </div>
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`clause-input-${c.category}`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(c.category, f); }} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`clause-upload-${c.category}`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{has && !c.default ? "Sostituisci" : "Carica PDF"}
        </Button>
        {has && <Button size="sm" variant="outline" onClick={() => onView(c.category)} data-testid={`clause-view-${c.category}`}><Eye className="h-4 w-4" /></Button>}
        {has && !c.default && (
          <ConfirmDelete onConfirm={() => onDelete(c.category)} title={`Rimuovere le condizioni ${c.category}?`} testId={`clause-del-${c.category}`}>
            <Trash2 className="h-4 w-4" />
          </ConfirmDelete>
        )}
      </div>
    </div>
  );
};

export const ClauseFiles = () => {
  const api = useApi();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/settings/clauses").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onUpload = async (cat, file) => {
    setBusy(cat);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/settings/clauses/${cat}`, fd); toast.success(`Condizioni ${cat} caricate`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const onView = async (cat) => {
    try {
      const r = await api.get(`/settings/clauses/${cat}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (cat) => {
    try { await api.delete(`/settings/clauses/${cat}`); toast.success("File rimosso"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-lg" data-testid="clause-files-section">
      <div className="p-5 border-b border-slate-800">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" />Condizioni generali per categoria</h2>
        <p className="text-xs text-slate-400 mt-1">Carica un PDF per ogni categoria: le sue pagine vengono aggiunte in fondo al preventivo, con header, footer e numero di pagina.</p>
      </div>
      <div className="divide-y divide-slate-800">
        {list.map((c) => <ClauseRow key={c.category} c={c} busy={busy === c.category} onUpload={onUpload} onView={onView} onDelete={onDelete} />)}
      </div>
    </section>
  );
};
